import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { ApiError, unwrap } from "../api/client.js";
import { AppShell } from "../components/AppShell.js";
import { Field, buttonClass, inputClass } from "../components/Field.js";
import { Turnstile } from "../components/Turnstile.js";
import { fetchMe, homePathFor, sessionKey, useSession } from "../lib/session.js";

export function LoginPage() {
  const { data: me, isPending } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const onToken = useCallback((t: string) => setCaptchaToken(t), []);

  const signIn = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/auth/sign-in/email", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password, turnstileToken: captchaToken }),
      });
      return unwrap<unknown>(res);
    },
    onSuccess: async () => {
      // Bypass react-query's cache entirely: `queryClient.fetchQuery({ queryKey: sessionKey })`
      // can resolve with the stale pre-login `null` still inside useSession's staleTime window,
      // which would bounce a user who just authenticated successfully straight back to /login.
      const me = await fetchMe();
      queryClient.setQueryData(sessionKey, me);
      navigate(me ? homePathFor(me.role) : "/", { replace: true });
    },
  });

  if (!isPending && me) return <Navigate to={homePathFor(me.role)} replace />;

  return (
    <AppShell title="로그인">
      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          signIn.mutate();
        }}
      >
        <Field label="이메일" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="비밀번호" htmlFor="password">
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            className={inputClass}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Turnstile onToken={onToken} />
        {signIn.error ? (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {signIn.error instanceof ApiError
              ? signIn.error.message
              : "로그인에 실패했습니다. 이메일과 비밀번호를 확인해 주세요."}
          </p>
        ) : null}
        <button type="submit" className={buttonClass} disabled={signIn.isPending || !captchaToken}>
          {signIn.isPending ? "로그인 중…" : "로그인"}
        </button>
        <p className="mt-4 text-center text-sm text-stone-500">
          계정은 초대를 통해서만 만들 수 있습니다.
        </p>
      </form>
    </AppShell>
  );
}
